import { useReprocessManifestCriticalGeometry } from '@/hooks/api/assets/useReprocessManifestCriticalGeometry';
import { usePutCriticalGeometry } from '@/hooks/api/assets/usePutCriticalGeometry';
import { RefObject, useCallback, useState } from 'react';
import { AssetType } from '@physna/core/domain/asset/asset-type';

const getIframeMessagingContext = (iframeReference: RefObject<HTMLIFrameElement | null>) => {
  const iframeElement = iframeReference.current!;
  const iframeWindow = iframeElement.contentWindow!;
  const targetOrigin = new URL(iframeElement.src).origin;
  return { iframeWindow, targetOrigin };
};

export function useCriticalGeometryPut({
  assetId,
  tenantId,
  iframeRef,
  assetType,
}: {
  assetId?: string;
  tenantId?: string;
  iframeRef: RefObject<HTMLIFrameElement | null>;
  assetType?: string;
}) {
  const { reprocessManifestCriticalGeometryAsync } = useReprocessManifestCriticalGeometry();
  const { putCriticalGeometryAsync } = usePutCriticalGeometry();
  const [isPutInFlight, setIsPutInFlight] = useState(false);

  const requestSelectedReleaseUnitVector = useCallback(
    async (parameters: {
      iframeWindow: Window;
      targetOrigin: string;
    }): Promise<[number, number, number] | undefined> => {
      const { iframeWindow, targetOrigin } = parameters;
      const requestId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;

      return await new Promise(resolve => {
        const timeout = globalThis.setTimeout(() => {
          globalThis.removeEventListener('message', onMessage);
          resolve(void 0);
        }, 750);

        const onMessage = (event: MessageEvent) => {
          const data = event.data as unknown;
          if (typeof data !== 'object' || data === null) return;
          if (!('type' in data) || !('requestId' in data)) return;
          const { type, requestId: respId } = data as { type: unknown; requestId: unknown };
          if (type !== 'physna:cg:release-vector' || respId !== requestId) return;

          globalThis.clearTimeout(timeout);
          globalThis.removeEventListener('message', onMessage);

          const vec =
            'releaseUnitVector' in data ? (data as { releaseUnitVector?: unknown }).releaseUnitVector : undefined;
          const normalized =
            Array.isArray(vec) &&
            vec.length === 3 &&
            vec.every((v: unknown) => typeof v === 'number' && Number.isFinite(v))
              ? (vec as [number, number, number])
              : undefined;
          resolve(normalized);
        };

        globalThis.addEventListener('message', onMessage);
        iframeWindow.postMessage({ type: 'physna:cg:get-release-vector', requestId }, targetOrigin);
      });
    },
    []
  );

  const put = useCallback(async () => {
    if (!assetId || !tenantId) return;
    if (isPutInFlight) return;
    setIsPutInFlight(true);

    try {
      const { iframeWindow, targetOrigin } = getIframeMessagingContext(iframeRef);

      if (assetType === AssetType.MANIFEST) {
        await reprocessManifestCriticalGeometryAsync({ tenantId, assetId });
      } else {
        const releaseUnitVector = await requestSelectedReleaseUnitVector({ iframeWindow, targetOrigin });
        await putCriticalGeometryAsync({ tenantId, assetId, releaseUnitVector });
      }

      iframeWindow.postMessage({ type: 'physna:cg:reload-analysis' }, targetOrigin);
    } finally {
      setIsPutInFlight(false);
    }
  }, [
    assetId,
    assetType,
    iframeRef,
    isPutInFlight,
    putCriticalGeometryAsync,
    reprocessManifestCriticalGeometryAsync,
    requestSelectedReleaseUnitVector,
    tenantId,
  ]);

  return { put, isPutInFlight };
}
